import React, { useEffect, useState } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { useUser } from '../components/UserContext';
import { useApiClient } from "../components/useApiClient";
import { useNavigate, useLocation } from "react-router";
import { loginRequest } from "../authConfig";
import homeVideo from "../assets/homepage_video.mp4";
import "./HomePage.css";
import TermsConsentModal from "../components/Eula/TermsConsentModal";
import { Outlet } from "react-router";

const HeroSection: React.FC = () => {
    const { instance } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const navigate = useNavigate();
    const location = useLocation();
    const redirectTo = (location.state as any)?.from || "/gallery";
    const { user, getAccessToken, refreshUser } = useUser();
    const { get, put, post, delete: del } = useApiClient();

    const [showTermsModal, setShowTermsModal] = useState(false);
    const [hasAcceptedLatestTerms, setHasAcceptedLatestTerms] = useState<boolean>(false);

    //Get EULA Version
    useEffect(() => {

        type EulaDetails = {
            version: string | null;
            date: string | null;
        };

        const getData = async () => {
            const accessToken = await getAccessToken();

            if (accessToken && user) {
                const response = await get<EulaDetails>(`eula/version`, accessToken);

                if (response.version == user.termVersionAccepted && user.termVersionAccepted != null) {
                    setHasAcceptedLatestTerms(true);
                } else {
                    setShowTermsModal(true);
                    setHasAcceptedLatestTerms(false);
                }
            }
        }

        getData();

    }, [user]);

    const handleTermsAccepted = () => {
        if (!user) return;

        refreshUser();
        setHasAcceptedLatestTerms(true);
        setShowTermsModal(false);

        navigate(redirectTo, { replace: true });
    };

    const handleLogout = () => {
        setShowTermsModal(false);
    };


    const handleLogin = () =>
        instance.loginRedirect({
            ...loginRequest,
            redirectStartPage: "/gallery"
        });

    const handleSignUp = () =>
        instance.loginRedirect({
            ...loginRequest,
            prompt: "create",
            redirectStartPage: "/"
        });

    const handleViewGallery = () => {
        if (hasAcceptedLatestTerms) {
            navigate(redirectTo, { replace: true })
        } else {
            setShowTermsModal(true);
        }

    };



    return (
        <section className="home-section hero-section">
            <header className="home-hero-header">
            <h1>Art Management Hub</h1>
            </header>

            <div className="home-hero-content">
                <h1>Clarity and control for art collections</h1>
                <p>
                    A secure platform for you to manage and curate
                    art and private collections
                </p>

                <div className="hero-cta-group">
                    {isAuthenticated ? (
                        <button onClick={handleViewGallery}>
                            View your collection
                        </button>
                    ) : (
                        <>
                            <button className="primary-btn" onClick={handleLogin}>
                                Log in to your collection
                            </button>
                                <button className="primary-btn" onClick={handleSignUp}>
                                {/*//=>*/}
                                {/*//window.location.href =*/}
                                {/*//"mailto:collections.manager@artmanagementhub.com?subject=Art%20platform%20access"}>*/}
                                Sign Up
                            </button>
                         </>
                    )}
                </div>
            </div>
            <TermsConsentModal
                isOpen={showTermsModal}
                userName={user?.displayName}
                onAccepted={handleTermsAccepted}
                onLogout={handleLogout}
            />
            {/* Block access to secure content until terms accepted */}
            {!showTermsModal && (
                <div className="secure-app-shell">
                    <Outlet />
                </div>
            )}
        </section>
    );
};

const ProblemSection: React.FC = () => (
    <section className="home-section">
        <h2>Managing art across families, institutions, collections and portfolios can be complex</h2>
        <p>
            Art collections are often spread across jurisdictions,
            residences, and custodians — with data held in spreadsheets, files,
            emails, and various systems. This creates risk, inefficiency, and
            a lack of clarity when decisions matter.
        </p>
        <ul className="bullet-list">
            <li>Fragmented records and documentation</li>
            <li>Valuations difficult to track or compare</li>
            <li>Limited visibility for principals and advisors</li>
            <li>Sensitive data without clear access controls</li>
        </ul>
    </section>
);

const RoleSection: React.FC = () => (
    <section className="home-section">
        <h2>A single source of truth for private collections</h2>
        <p>
            The Art Management Hub provides a structured, secure view of art and collectible
            assets, supporting governance, reporting, and informed decision-making
            without disrupting existing advisor or working practices.
        </p>
    </section>
);

const CapabilitiesSection: React.FC = () => (
    <section className="home-section">
        <h2>Capabilities that matter to art advisors and family offices</h2>
        <div className="card-grid">
            <div className="home-info-card">
                <h3>Structured collection management</h3>
                <p>
                    Maintain a complete catalogue of artworks, images, documents, and
                    ownership details in a single private and secure environment.
                </p>
            </div>
            <div className="home-info-card">
                <h3>Valuation insight</h3>
                <p>
                    Track insurance, advisory, and market valuations by
                    portfolio, currency, artist, or location.
                </p>
            </div>
            <div className="home-info-card">
                <h3>Location and custody visibility</h3>
                <p>
                    Understand exactly where assets are held across countries, buildings,
                    storage facilities, and loans.
                </p>
            </div>
            <div className="home-info-card">
                <h3>Provenance and documentation</h3>
                <p>
                    Securely store and organise invoices, certificates, condition reports,
                    and related correspondence.
                </p>
            </div>
            <div className="home-info-card">
                <h3>Privacy and access control</h3>
                <p>
                    Role-based access ensures the right people see the right information.
                </p>
            </div>
            <div className="home-info-card">
                <h3>Custom portfolios and controlled visibility</h3>
                <p>
                    Create tailored portfolios from your collection, with the ability
                    to restrict access so specific users only see selected artworks.
                </p>
            </div>
        </div>
    </section>
);

const StewardshipSection: React.FC = () => (
    <section className="home-section">
        <h2>Built for long-term stewardship.</h2>
        <p>
            Art held and managed by family offices is rarely transactional. This platforms enables you to manage and maintain all asset information in one place.
        </p>
        <p>
            The platform is designed to support continuity, discretion, and informed
            oversight over the long-term.
        </p>
    </section>
);

const SecuritySection: React.FC = () => (
    <section className="home-section">
        <h2>Security and discretion by design.</h2>
        <ul className="bullet-list">
            <li>Private, customer-separated collections</li>
            <li>Secure cloud storage for images and documents</li>
            <li>Full audit history of changes</li>
            <li>Designed with data protection in mind</li>
        </ul>
    </section>
);

const CallToActionSection: React.FC = () => (
    <section className="home-section final-cta">
        <h2>Do you need a modern platform for managing art collections?</h2>
        <p>
            If you are responsible for the oversight of a private art collection and
            would like to explore whether the Art Management Hub is right for you, request access today.
        </p>
        <button className="primary-btn" onClick={() =>
            window.location.href =
            "mailto:collections.manager@artmanagementhub.com?subject=Art%20platform%20access"
        }>
            Request access
        </button>
    </section>
);

const Footer: React.FC = () => {
    const navigate = useNavigate();
    return (
    
    <section className="home-section hero-section">
        <footer className="home-footer">
        <div className="footer-left">
            <span className="footer-brand">Art Management Hub</span>
            <span className="footer-tagline"></span>
        </div>
        <div className="footer-links">
                <button className="home-link" onClick={() => navigate("/privacy")}>
                    Privacy
                    </button>
                    <button className="home-link" onClick={() => navigate("/terms")}>
                        Terms
                    </button>
                    <button className="home-link" onClick={() =>
                        window.location.href =
                        "mailto:collections.manager@artmanagementhub.com?subject=Art%20Platform%20Enquiry"
                    }>
                        Contact Us
                    </button>
        </div>
        </footer>
    </section>
)};

const HomePage: React.FC = () => {
    

    return (
        <div className="home">
            <video
                className="home-video"
                src={homeVideo}
                autoPlay
                loop
                muted
                playsInline
            />
            <main className="home-overlay">
                <HeroSection />
                <RoleSection />
                <ProblemSection />
                <CapabilitiesSection />
                <SecuritySection />
                <CallToActionSection />
                <Footer />

            </main>
        </div>
    );
};

export default HomePage;
